import { Grid, GridColumn as Column, GridColumnMenuCheckboxFilter, GridCellProps, GridItemChangeEvent, GridToolbar, GridColumnResizeEvent, GridDataStateChangeEvent, GridColumnMenuFilter } from '@progress/kendo-react-grid';
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import { MyCommandCell } from '../components/synoptic/MyCommandCellComponent';
import { AccessToken } from '../config/AccessToken';
import { Button } from '@progress/kendo-react-buttons';
import { useNavigate } from 'react-router-dom';

import { process as kendoProcess } from "@progress/kendo-data-query";
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { GridPDFExport } from '@progress/kendo-react-pdf';
import { fileCsvIcon, fileExcelIcon, filePdfIcon, filterIcon, saveIcon, uploadIcon, warningTriangleIcon } from '@progress/kendo-svg-icons';
import { CSVLink } from 'react-csv';
import { json2csv } from 'json-2-csv';
import { Popup } from '@progress/kendo-react-popup';
import { loadingPanel } from '../components/LoadingPanel';
import { ConfiguratorToggleColumnsGrid } from '../components/ConfiguratorToggleColumnsGrid';
import { SvgIcon } from '@progress/kendo-react-common';
import { Switch } from '@progress/kendo-react-inputs';
import { alarm } from '../assets/alarm';
import { EditDescriptor, HeaderThElement } from '@progress/kendo-react-data-tools';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { DatePicker, DateTimePicker, TimePicker } from '@progress/kendo-react-dateinputs';

/**
 * SynopticDevices component manages and displays a grid of device information.
 * It includes functionalities for device list retrieval, filtering, sorting,
 * column management, and exporting data to Excel, PDF, and CSV formats.
 *
 * The component interacts with a server to fetch device data, allows editing
 * device names, and provides a UI for toggling column visibility. It also 
 * supports pagination and responsive design adjustments based on grid width.
 *
 * State management is used extensively to manage devices, columns, data states,
 * and UI states such as loading and error handling.
 *
 * Utilizes KendoReact components for grid and toolbar functionalities, and
 * React's useEffect hook to initialize device data on component mount.
 */

const editField = "inEdit";

const EditingCell = (props:any) => {
  const { edit, update, cancel, editField } = props;
  return (
      <MyCommandCell
          {...props}
          edit={edit}
          update={update}
          cancel={cancel}
          editField={editField}
      />
  );
};

const LinkMapCell = (props: any) => {

  const navigate = useNavigate()

  const localizationService = useLocalization();
  let lastPositionDate = props?.dataItem?.lastPositionDate;

  const showMap = () => {
    let deviceMapId = props?.dataItem?.mapId;
    let deviceGroupId = props?.dataItem?.groupId;
    let deviceMac = props?.dataItem?.mac;

    if (deviceMapId != null) {
      localStorage.setItem("deviceMapId", deviceMapId);
    }
    if (deviceGroupId != null) {
      localStorage.setItem("deviceGroupId", deviceGroupId);
    }
    if (deviceMac != null) {
      localStorage.setItem("deviceMac", deviceMac);
    }
    navigate("/home/map")
  }

  return (
    <td className="k-command-cell" style={{textAlign: 'center'}}>
      <button
        id='map'
        className="k-icon k-font-icon k-i-globe"
        style={{ color: '#198792', margin: '8px', border: 'none', background: 'none', outline: 'none' }}
        disabled={lastPositionDate != 0 ? false : true}
        onClick={() => showMap()}
      >
        <a
          id='map'
          title={ lastPositionDate != 0 ?
            localizationService.toLanguageString('synoptic_devices.map', 'NOT_FOUND')
            :
            localizationService.toLanguageString('synoptic_devices.no_position_found', 'NOT_FOUND')
          }
        />
      </button>
    </td>
  );
};

const LinkDeviceManagement = (props: any) => {

  const localizationService = useLocalization();
  const navigate = useNavigate()
  let deviceMac: any = props?.dataItem?.mac;

  return (
    <td className="k-command-cell" style={{textAlign: 'center'}}>
      <button
        id='deviceManagement'
        className="k-icon k-font-icon k-i-gears"        
        style={{ color: '#198792', margin: '8px', border: 'none', background: 'none', outline: 'none' }}
        onClick={() => navigate("/home/configuration/devices/devices-management", { state: { deviceMac: deviceMac } })}
      >
        <a
          id='deviceManagement'
          title={localizationService.toLanguageString('configuration.devices.devices_management', 'NOT_FOUND')}
        />
      </button>
    </td>
  );
};

export const SynopticDevices = () => {

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const [edit, setEdit] = useState<EditDescriptor>({});

  const [columns, setColumns] = useState<any>([]);

  const [devices, setDevices] = useState<any>([]);
  const [devicesOn, setDevicesOn] = useState<any>([]);
  const [countDevicesOn, setCountDevicesOn] = useState(0);

  const [error, setError] = useState('');

  const [loading, setLoading] = useState(false);

  const localizationService = useLocalization();
  const [instance, accessTokenRequest] = AccessToken();

  const userEmail = localStorage.getItem('email');
  const statusDevice:any = localStorage.getItem('statusDevice');

  const [liveStatus, setLiveStatus] = useState<any>(false);
  const [onlyBound, setOnlyBound] = useState<any>(false);
  
  const [filterName, setFilterName] = useState<any>(() => {
    const filterNameSynoptic = localStorage.getItem('filterNameSynoptic');
    return filterNameSynoptic ? JSON.parse(filterNameSynoptic) : [];
  });

  const [editMode, setEditMode] = useState<any>(false);

  const refreshAPI:any = localStorage.getItem('refreshAPI') ?? 30;
  
  let languageId = localStorage.getItem('languageId');
    
  useEffect(() => {
    
    const liveStatus:any = localStorage.getItem('liveStatus');
    const onlyBound:any = localStorage.getItem('onlyBound');
    
    // Set a timeout to call getDeviceList with delay based on refreshAPI
    const intervalId  = setInterval(() => {
      const filteredColumn:any = localStorage.getItem('filterOpenedColumn');
      if(refreshAPI != null && !editMode && filteredColumn != 'openedColumn'){
        if(liveStatus == 'true'){
          onToggleLive(true)
        }else{
          onToggleLive(false)
        }
      }
    }, refreshAPI * 1000); // Delay in milliseconds
      
    return () => clearInterval(intervalId );
  }, [devicesOn, editMode]);

  
  const formatDate = (date: any) => {
    if(date != undefined){
      // Split the date and time
      const [day, month, year] = date?.split(" ")[0]?.split("/"); 

      let dateParts = date?.split(" ");
      const timeValues:any = dateParts[1]?.split(":");
      
      let hour = parseInt(timeValues[0], 10);
      let minute = parseInt(timeValues[1], 10);
      let second = parseInt(timeValues[2], 10);
      
      // Convert the string to a Date object
      let dateConvert = new Date(Date.UTC(year, month-1, day, hour, minute, second))
      
      // Convert the date to the user's local timezone
      let localDate = dateConvert?.toLocaleString(navigator.language).replace(/,/g, '')
      
      const timeConvert = localDate?.split(" ")[1];
      const timeValuesConvert:any = timeConvert?.split(":");
      const formattedHours = String(timeValuesConvert[0]).padStart(2, '0'); // Add leading zero if needed
      const formattedMinutes = String(timeValuesConvert[1]).padStart(2, '0');
      const formattedSeconds = String(timeValuesConvert[2]).padStart(2, '0');
      const formattedTime = `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
      
      if(navigator.language == 'en' || navigator.language == 'en-US' || navigator.language == 'en-GB'){
        const [monthConvert, dayConvert, yearConvert] = localDate?.split(" ")[0].split("/"); 
        const formattedMonth = String(monthConvert).padStart(2, '0'); // Add leading zero if needed
        const formattedDay = String(dayConvert).padStart(2, '0');
        const validDate = `${yearConvert}-${formattedMonth}-${formattedDay}T${formattedTime}`;
        return new Date(validDate);
      }
      else{
        const [dayConvert, monthConvert, yearConvert] = localDate?.split(" ")[0].split("/"); 
        const formattedMonth = String(monthConvert).padStart(2, '0'); // Add leading zero if needed
        const formattedDay = String(dayConvert).padStart(2, '0');
        const validDate = `${yearConvert}-${formattedMonth}-${formattedDay}T${timeConvert}`;
        return new Date(validDate);
      }
    }else{
      return 0
    }
  }

  const formatDateFromIsoToString = (isoString:any) => {
    const date = new Date(isoString);
  
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
  
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  const getDeviceList = async (onlyBound?: boolean) => {
    let languageId = localStorage.getItem('languageId');

    setLoading(true)

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      
      try {
        const res = await axios.get(`${SERVER_URL}/device/list`, { headers })
        const data = res?.data   

        if(data?.data?.length == 0) return

        let deviceOn: any = [] 
        let devices: any = [] 
        let dataState: any = data

          
        // format date in data.data
        data?.data?.forEach((device: any) => {

          if(device?.isEnabled){

            if(onlyBound){
              if(device?.name == '' || device?.name == null){
                return
              }
            }
            
            if(device?.temperature != null){
              device.temperature = device?.temperature
            }
            
            if(device?.battery != null){
              device.battery = device?.battery 
            }
          
            device.localTime = formatDate(device?.localTime);            
            device.dateTime = formatDate(device?.dateTime);                     
            device.lastPositionDate = formatDate(device?.lastPositionDate);                        
            device.localLastPositionDate = formatDate(device?.localLastPositionDate);

            const currStatusDeviceTime:any = getDateRange(statusDevice)
            const localTimeString = device.dateTime.toLocaleString(navigator.language, { 
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true // Force 12-hour format with AM/PM
            });  

            
            if(device.dateTime == 0){
              device.status = 0
            }

            if(compareDateTimes(localTimeString, currStatusDeviceTime) != 1){
              device.status = 0
            }else{
              device.status = 1
            }
            
            deviceOn.push(device)
          }
        })          
        
        setCountDevicesOn(deviceOn?.length)        
        
        dataState.skip = 0;
        dataState.take = 10;

        setDevicesOn(deviceOn);
        setDevices(devices)
        setDataState(dataState)

        let customInfo: any = [];
        //const customInfoDictionary: any = [];

        deviceOn?.map((device: any) => {
          if (device.customInfo != '' && device.customInfo != null) {
            customInfo = JSON.parse(device.customInfo)
            delete customInfo["ProximitySensor"];
            delete customInfo["SensorProximity"];
            device.customInfo = JSON.stringify(customInfo);
          }
          //customInfoDictionary.push(device.customInfoDictionary)
        })
        
        //const customInfoColumns: any = [];
        //const customInfoDictionaryColumns: any = [];
        //customInfoDictionary.map((customInfo: any) => {
        //  if(customInfo != null){
        //    const keys = Object.keys(customInfo);
        //    keys.forEach(key => {
        //      if(key != 'ProximitySensor' && key != 'SensorProximity'){
        //        customInfoDictionaryColumns.push({ field: key, title: key, cell: customInfoColumnsCell, filterable: false, editable: false, show: true });
        //      }
        //    });
        //  }
        //})

        /* //************************************ */

        //const currColumns: any = [
        //  //{ name:'editingCell', cell:{editingCell}, minWidth: 160, editable: false, filterable: false, show: true },
        //  { field: 'status', title: localizationService.toLanguageString('synoptic_devices.status', 'NOT_FOUND'), editable: false, filterable: false, show: true },
        //  { field: 'mac', title: "Mac", columnMenu: ColumnMenuCheckboxFilter, editable: false, show: true },
        //  { field: 'name', title: localizationService.toLanguageString('synoptic_devices.device_name', 'NOT_FOUND'), editor:"text", columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: true, show: true, hidden:false },
        //  { field: 'groupName', title: localizationService.toLanguageString('synoptic_devices.group_name', 'NOT_FOUND'), columnMenu: GroupCellWithColorComponent, filterable: false, editable: false, show: true, hidden:false },
        //  { field: 'jobName', title: localizationService.toLanguageString('synoptic_devices.job', 'NOT_FOUND'), columnMenu: JobCellWithColorComponent, filterable: false, editable: false, show: true, hidden:false },
        //  { field: 'zone', title: localizationService.toLanguageString('synoptic_devices.zone', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: false, editable: false, show: true, hidden:false },
        //  { field: 'deviceTypeName', title: localizationService.toLanguageString('synoptic_devices.device_type_name', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, editable: false, show: true, hidden:false },
        //  { field: 'temperature', title: localizationService.toLanguageString('synoptic_devices.temperature', 'NOT_FOUND'), filter:"numeric", filterable: true, editable: false, show: true, hidden:false },
        //  { field: 'localTime', title: localizationService.toLanguageString('synoptic_devices.local_time', 'NOT_FOUND'), format: `{0:dd/MM/yyyy HH:mm:ss}`, filter:"date", filterable: true, editable: false, show: false, hidden:false },
        //  { field: 'dateTime', title: localizationService.toLanguageString('synoptic_devices.date_time', 'NOT_FOUND'), format:"{0:dd/MM/yyyy HH:mm:ss}", filter:"date", filterable: true, editable: false, show: true, hidden:false },
        //  { field: 'lastPositionDate', title: localizationService.toLanguageString('synoptic_devices.last_position_date', 'NOT_FOUND'), format:`{0:dd/MM/yyyy HH:mm:ss}`, filter:"date", filterable: true, editable: false, show: true, hidden:false },
        //  { field: 'localLastPositionDate', title: localizationService.toLanguageString('synoptic_devices.local_last_position_date', 'NOT_FOUND'), format:`{0:dd/MM/yyyy HH:mm:ss}`, filter:"date", filterable: true, editable: false, show: false, hidden:false },
        //  { field: 'customInfo', title: localizationService.toLanguageString('synoptic_devices.custom_info', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: false, show: true, hidden:false },
        //  { field: 'battery', title: localizationService.toLanguageString('synoptic_devices.battery', 'NOT_FOUND'), cell: BatteryCellWithBackGroundComponent, filterable: true, editable: false, show: true },
        //  { field: 'inCharge', title: localizationService.toLanguageString('synoptic_devices.inCharge', 'NOT_FOUND'), filterable: false, editable: false, show: true },
        //  { field: 'alarm', title: localizationService.toLanguageString('synoptic_devices.alarm', 'NOT_FOUND'), cell: AlarmCellWithBackGroundComponent, filterable: false, editable: false, show: true },
        //  //...customInfoDictionaryColumns
        //];
        
        //setColumns(currColumns);

        loadStateFromLocalStorage()
        setLoading(false)
        
        
      } catch (error: any) {
        setError(error);
        setLoading(false)
      }
    }

    if (sessionStorage.getItem('accessToken') != null) {
      let accessToken = sessionStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
    setLoading(false)
  }

  //call getDeviceList
  useEffect(() => {
      getDeviceList()    
  }, [])

  const editFieldName = async (deviceMac: any, deviceName: any, startDate: any) => {

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      const params = {
        deviceMac,
        deviceName,
        startDate
      };
      try {
        await axios.post(`${SERVER_URL}/device/binding/update`, params, { headers })
        //console.log('OK');
      } catch (error: any) {
        setError(error);
      }
    }
    if (sessionStorage.getItem('accessToken') != null) {
      let accessToken = sessionStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  const IsUnaryFilter = (operator:any) => {
    return ['isnull', 'isnotnull', 'isempty', 'isnotempty'].includes(operator);
  };
  
  const cellInputChange = (value:any, e:any, props:any) => {
    const defaultOperator = getDefaultOperator(props.operators);
    let operator = props.operator;
  
    if (!operator || IsUnaryFilter(operator)) {
      operator = defaultOperator;
    }
    if (value === null && operator === defaultOperator) {
      operator = '';
    }
  
    props.onChange({ value, operator, syntheticEvent: e });
  };
  
  const getDefaultOperator = (filterOperators:any) => {
    return filterOperators.length > 0 ? filterOperators[0].operator : '';
  };
  
  const cellOperatorChange = (event:any, currentValue:any, onChange:any) => {
    let value = currentValue;
    const item = event.target.value;
    if (!event.target.state?.opened) {
      return;
    }
    if (IsUnaryFilter(item.operator)) {
      value = null;
    }
    if (currentValue === null && !IsUnaryFilter(item.operator)) {
      value = undefined;
    }
    onChange({
      value,
      operator: item.operator,
      syntheticEvent: event.syntheticEvent,
    });
  };
  
  const [dateValue, setDateValue] = useState<Date | null>(new Date());
  
  const removeFilterByField = (filterObject: any, fieldName: any): any => {
    if (!filterObject || !filterObject.filters || !Array.isArray(filterObject.filters)) {
      return { logic: 'and', filters: [] };
    }
    
    const updatedFilters = filterObject.filters
    .map((item: any) => {
      // Se è il filtro da rimuovere
      if (item.field === fieldName) {
        return null;
            }
            
            // Se è un gruppo di filtri annidato
            if (item.filters && Array.isArray(item.filters)) {
              const updatedGroup = removeFilterByField(item, fieldName);
                // Mantieni solo se il gruppo ha ancora filtri
                if (updatedGroup.filters.length > 0) {
                    return updatedGroup;
                  } else {
                    return null;
                  }
                }
            return item;
        })
        .filter((item: any) => item !== null);

        // Sempre restituire un oggetto con 'logic' e 'filters'
        return {
          logic: filterObject.logic || 'and',
          filters: updatedFilters
    };
  };
  
  const GridColumnMenuFilterCell = (props:any) => {
    const gridColumnMenuFilterCell = useRef<any>(null);

    const handleClose = () => {
    // Esempio: mantieni focus su un elemento specifico
      setTimeout(() => {
        gridColumnMenuFilterCell.current?.focus();
      }, 0);
    };

    useEffect(() => {
      
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };
  
      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.field;
  
      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;
  
      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };
  
      setDataState(newState);
      setFilterName((prev: any) => prev.filter((item: any) => item !== field));
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    useEffect(() => {
      // Questo effetto scatta all'apertura del filtro
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    const [selectedOperator, setSelectedOperator] = useState(props.operator || 'eq');    

    const addFilter = (obj: any, filtro: any, logic = "or") => {
      // Trova il prossimo indice disponibile
      let keys = Object.keys(obj).map(k => parseInt(k));
      let nextIndex = keys.length > 0 ? Math.max(...keys) + 1 : 0;
  
      // Aggiungi il nuovo filtro
      obj[nextIndex.toString()] = {
          "logic": logic,
          "filters": [filtro]
      };
  
      return obj;
    }

    const containsDateTimeField = (filters:any):boolean => {
      if (!Array.isArray(filters)) return false;
  
      return filters.some((filter:any) => {
          if (filter.field === 'dateTime') {
              return true;
          } 
          // Se ha sotto-filtri, ricorsivamente cerca anche lì
          if (Array.isArray(filter.filters)) {
              return containsDateTimeField(filter.filters);
          }
          return false;
      });
    };

    const replaceOrNestDateTimeFilterAtTop = (filters: any[], newFilter: any): any[] => {
      let found = false;
  
      // Ricorsivamente sostituisce
      const recursiveReplace = (filtersArray: any[]): any[] => {
          return filtersArray.map((f: any) => {
              if (f.filters && Array.isArray(f.filters)) {
                  return { ...f, filters: recursiveReplace(f.filters) };
              } else if (f.field === 'dateTime') {
                  found = true;
                  return newFilter;
              }
              return f;
          });
      };
  
      let updatedFilters = recursiveReplace(filters);
  
      if (!found) {
          // Se non trovato, crea un nuovo gruppo 'or' con dateTime
          const dateTimeGroup = {
              logic: 'or',
              filters: [newFilter]
          };
  
          // Inserisci il gruppo dateTime sempre in posizione 0
          //updatedFilters = [dateTimeGroup, ...updatedFilters];
          addFilter(updatedFilters, dateTimeGroup, 'or');
      } 
      return updatedFilters;
    };
  

    const inputChange = (value:any, e:any) => {
      cellInputChange(value, e, props);

      setDateValue(value)

      if (value == null) {
        return;
      } 

      value.setUTCMilliseconds(0);
      value = value.toISOString()

      const filterObj = {   
        ...dataState?.filter
      };

      const newFilter = {
        "field": props.field,
        "operator": selectedOperator,
        "value": value
      };

      if(filterObj?.filters == undefined){
        filterObj.logic = 'and';
        filterObj.filters = [{                              
          logic: "or",
          filters:[ newFilter ]
        }]
      }else{
        if(containsDateTimeField(filterObj.filters)){
          filterObj.filters = replaceOrNestDateTimeFilterAtTop(filterObj.filters, newFilter);
        }else{
          addFilter(filterObj.filters, newFilter, 'or');
        }
      }
        
      setDataState((prevState: any) => ({
        ...prevState,
        filter: filterObj
      }));

      // Set filter name for tracking
      const filterName = props.field;
      
      setFilterName((prevFilterName:any) => {
        return [...prevFilterName, filterName];
      });
      
      // Salva subito lo stato aggiornato
      const newState = {
        dataState: {
          ...dataState,
          filter: filterObj
        },
        columns
      };
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(newState));

      getDeviceList();

    };    
  
    const operatorChange = (event:any) => {
      const newOperator = event.value.operator;
      setSelectedOperator(newOperator); // <-- Track selected operator
      cellOperatorChange(event, props.value, props.onChange);
    };
  
    const renderOperatorEditor = () => {
      const { operators } = props;

      const filteredOperators = operators.filter(
        (op: any) => op.operator !== 'isnull' && op.operator !== 'isnotnull'
      );
  
      const value = filteredOperators.find((item: any) => item.operator === selectedOperator) || null;
  
      return (
        <DropDownList
          value={value}
          onChange={operatorChange}
          data={filteredOperators}
          textField="text"
        />
      );
    };
  
    return (
      <div onClick={(e) => e.stopPropagation()}>
        {renderOperatorEditor()}

        {/*<DateTimePicker
          value={dateValue}
          onFocus={() => console.log("DateTimePicker focused")}
          onBlur={() => console.log("DateTimePicker blurred")}
          onChange={(e) => {
            console.log("DateTimePicker changed");
            inputChange(e.value, e.syntheticEvent)
          }}
        />*/}

        <DatePicker              
          ref={gridColumnMenuFilterCell}      
          value={dateValue}
          format="dd/MM/yyyy"
          formatPlaceholder={'formatPattern'}
          onChange={(e:any) => {
            e.syntheticEvent?.preventDefault?.();
            e.syntheticEvent?.stopPropagation?.();
            inputChange(e.value, e.syntheticEvent)
            setTimeout(() => {
              gridColumnMenuFilterCell.current?.focus();
            }, 0);
          }}
          onClick={(e) => {
            e.stopPropagation()
          }}
          onClose={handleClose}
        />
        
        <div style={{marginBottom: '10px'}}></div>

        <TimePicker
          value={dateValue}
          format="HH:mm:ss" // Include seconds in the format
          formatPlaceholder={'formatPattern'}
          onChange={(e) => inputChange(e.value, e.syntheticEvent)}
        />
      </div>
    );
  };



  const CustomGridFilterDateTime = (props:any) => {
    const {
      firstFilterProps
    } = props;

     
    return (
      <div>
        <GridColumnMenuFilterCell           
          {...firstFilterProps} 
        />
      </div>
    );
  };
   
  const ColumnMenuCheckboxFilter = (props: any) => {

    useEffect(() => {
  
      // Delegazione click su bottone reset
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };
  
      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.column.field;
  
      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;
  
      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters 
        }
      };
  
      setDataState(newState);
      setFilterName((prev: any) => prev.filter((item: any) => item !== field));
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };
    
    useEffect(() => {
      // Questo effetto scatta all'apertura del filtro
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    const handleFilterChange = (filterValue: any) => {

      const filterName = props.column.field;      
      
      // Ricorsivamente verifica se il filtro contiene il campo
      const containsFilterField = (filter: any): boolean => {
        if (!filter) return false;

        if (Array.isArray(filter.filters)) {
          return filter.filters.some((f:any) => containsFilterField(f));
        }

        return filter.field === filterName;
      };

      const shouldRemove = !filterValue || !containsFilterField(filterValue);

      if (shouldRemove) {
        setFilterName((prevFilterName: any) =>
          prevFilterName.filter((item: any) => item !== filterName)
        );
        localStorage.removeItem('filterOpenedColumn');
        return;
      }else {
        setFilterName((prevFilterName: any) =>
          prevFilterName.includes(filterName)
            ? prevFilterName
            : [...prevFilterName, filterName]
        );
  
        const newFilter: any = filterValue;
        setDataState((prevState: any) => ({
          ...prevState,
          filter: newFilter
        }));
  
        // Salvataggio immediato dello stato nel localStorage
        const newState = {
          dataState: {
            ...dataState,
            filter: newFilter
          },
          columns
        };
        localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(newState));        
      }
    };

    return (
      <div>
        <GridColumnMenuCheckboxFilter 
          {...props} 
          data={devicesOn} 
          expanded={true} 
          searchBoxFilterOperator={'contains'} 
          onFilterChange={handleFilterChange}
          //onCloseMenu={() => {
          //  const filterName = props.column.field;      
          //  setFilterName((prevFilterName:any) => {
          //    return prevFilterName.filter((item:any) => item !== filterName);
          //  });
          //  localStorage.removeItem('filterOpenedColumn');
          //}}
        />
      </div>
    );
  }
  
  const CustomInfoCheckboxFilter = (props: any) => {

    useEffect(() => {
  
      // Delegazione click su bottone reset
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };
  
      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.column.field;
  
      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;
  
      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);
  
      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };
  
      setDataState(newState);
      setFilterName((prev: any) => prev.filter((item: any) => item !== field));
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    useEffect(() => {
      // Questo effetto scatta all'apertura del filtro
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    let currDevice:any = []
    devicesOn.map((device: any) => {
      if(device.customInfoDictionary != null && device.customInfoDictionary != ''){
        currDevice.push(device)
      }
    })

    return (
      <div>
        <GridColumnMenuCheckboxFilter 
          {...props} 
          data={currDevice} 
          expanded={true} 
          searchBoxFilterOperator={'contains'} 
        />
      </div>
    );
  }

  const ColumnMenuNumericFilter = (props: any) => {

    useEffect(() => {
      // Questo effetto scatta all'apertura del filtro
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    useEffect(() => {  
      // Delegazione click su bottone reset
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };
  
      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.column.field;
  
      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;
  
      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);
  
      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };
  
      setDataState(newState);
      setFilterName((prev: any) => prev.filter((item: any) => item !== field));
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    if (!props || !props.column) {
      return null;
    }
    
    const customFilterOperators:any = {
      numeric: [
          { text: 'grid.filterEqOperator', operator: 'eq' },
          { text: 'grid.filterNotEqOperator', operator: 'neq' },
          { text: 'grid.filterGteOperator', operator: 'gte' },
          { text: 'grid.filterGtOperator', operator: 'gt' },
          { text: 'grid.filterLteOperator', operator: 'lte' },
          { text: 'grid.filterLtOperator', operator: 'lt' }
     
      ]
    };

    const handleFilterChange = (filterValue: any) => {

      const filterName = props.column.field;      
      
      // Ricorsivamente verifica se il filtro contiene il campo
      const containsFilterField = (filter: any): boolean => {
        if (!filter) return false;

        if (Array.isArray(filter.filters)) {
          return filter.filters.some((f:any) => containsFilterField(f));
        }

        return filter.field === filterName;
      };

      const shouldRemove = !filterValue || !containsFilterField(filterValue);

      if (shouldRemove) {
        setFilterName((prevFilterName: any) =>
          prevFilterName.filter((item: any) => item !== filterName)
        );
        localStorage.removeItem('filterOpenedColumn');
        return;
      }else {
        setFilterName((prevFilterName: any) =>
          prevFilterName.includes(filterName)
            ? prevFilterName
            : [...prevFilterName, filterName]
        );
  
        const newFilter: any = filterValue;
        setDataState((prevState: any) => ({
          ...prevState,
          filter: newFilter
        }));
  
        // Salvataggio immediato dello stato nel localStorage
        const newState = {
          dataState: {
            ...dataState,
            filter: newFilter
          },
          columns
        };
        localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(newState));        
      }
    };

    return (
      <GridColumnMenuFilter
        {...props}
        expanded={true} 
        hideSecondFilter={true}
        filterOperators={customFilterOperators}
        onFilterChange={handleFilterChange}  
        //onCloseMenu={() => {
        //  const filterName = props.column.field;      
        //  setFilterName((prevFilterName:any) => {
        //    return prevFilterName.filter((item:any) => item !== filterName);
        //  });
        //  localStorage.removeItem('filterOpenedColumn');
        //}}
      />
    );
  };
  
  const StatusCellWithColorComponent = (props: GridCellProps) => {
    
    const statusDevice:any = props.dataItem.status;
    if(statusDevice == 0){
      return (
        <td>
          <span className='statusRed'></span>
        </td>
      );
    }else{
      
      if(statusDevice != 1){
        return (
          <td>
            <span className='statusRed'></span>
          </td>
        );
      }
      else {
        return (
          <td>
            <span className='statusGreen'></span>
          </td>
        );
      }
    }
  };

  const InChargeCellWithColorComponent = (props: GridCellProps) => {
    const inCharge = props.dataItem.inCharge;
    if (inCharge == null) {
      return (
        <td 
          style={{ textAlign: 'center' }} 
          title={localizationService.toLanguageString('synoptic_devices.warning_battery', 'NOT_FOUND')}
        >
          <SvgIcon icon={warningTriangleIcon} size="medium" />
        </td>
      );
    }
    else if(inCharge == 0){
      return (
        <td>
          <span 
            className='inChargeRed' 
            title={localizationService.toLanguageString('synoptic_devices.inChargeRed', 'NOT_FOUND')}
        ></span>
        </td>
      );
    }
    else {
      return (
        <td>
          <span 
            className='inChargeGreen' 
            title={localizationService.toLanguageString('synoptic_devices.inChargeGreen', 'NOT_FOUND')}
          ></span>
        </td>
      );
    }
  };
  
  const GroupCellWithColorComponent = (props: any) => {        
    const groupName = props.dataItem.groupName    
    const groupColor = props.dataItem.groupColor    
    return(
      <td>
        <div style={{display: 'flex'}}>
          <div className='groupColor' style={{ backgroundColor: groupColor }} />
          <span>{groupName}</span>
        </div>
      </td>
    )     
  };

  const JobCellWithColorComponent = (props: any) => {    
    const jobName = props.dataItem.jobName    
    const jobColor = props.dataItem.jobColor    
    return(
      <td 
        //style={{display: 'flex'}}
      >
        <div style={{display: 'flex'}}>
          <div className='jobColor' style={{ backgroundColor: jobColor }} />
          <span style={{minWidth: '100px'}}>{jobName}</span>
        </div>
      </td>
    )     
  };

  const TemperatureCellWithBackGroundComponent = (props: any) => {
    const temperatureValue = props?.dataItem?.temperature;  
    if(temperatureValue != null){   
      return(
        <td>
          <span className='k-table-row k-master-row temperature'>{temperatureValue} °C</span>
        </td>
      )
    } else {
      return(
        <td><span className='k-table-row k-master-row temperature' /></td>
      )
    }
  };
  
  const DateTimeCellWithBackGroundComponent = (props: any) => {
    const dateTimeValue:any = formatDateFromIsoToString(props?.dataItem?.dateTime);  
    if(dateTimeValue != null && !dateTimeValue.includes('1970')){   
      return(
        <td>
          <span className='k-table-row k-master-row date-time'>{dateTimeValue}</span>
        </td>
      )
    } else {
      return(
        <td><span className='k-table-row k-master-row date-time' /></td>
      )
    }
  };
  
  const LastPositionDateCellWithBackGroundComponent = (props: any) => {
    const lastPositionDateValue:any = formatDateFromIsoToString(props?.dataItem?.lastPositionDate);  
    if(lastPositionDateValue != null && !lastPositionDateValue.includes('1970') && !lastPositionDateValue.includes('1753')){   
      return(
        <td>
          <span className='k-table-row k-master-row last-position'>{lastPositionDateValue}</span>
        </td>
      )
    } else {
      return(
        <td><span className='k-table-row k-master-row last-position' /></td>
      )
    }
  };
  
  const LocalLastPositionDateCellWithBackGroundComponent = (props: any) => {
    const localLastPositionDateValue:any = formatDateFromIsoToString(props?.dataItem?.localLastPositionDate);  
    if(localLastPositionDateValue != null && !localLastPositionDateValue.includes('1970') && !localLastPositionDateValue.includes('1753')){   
      return(
        <td>
          <span className='k-table-row k-master-row local-last-position'>{localLastPositionDateValue}</span>
        </td>
      )
    } else {
      return(
        <td><span className='k-table-row k-master-row local-last-position' /></td>
      )
    }
  };

  const LocalTimeCellWithBackGroundComponent = (props: any) => {
    const localTimeValue:any = formatDateFromIsoToString(props?.dataItem?.localTime);  
    if(localTimeValue != null && !localTimeValue.includes('1970') && !localTimeValue.includes('1753')){   
      return(
        <td>
          <span className='k-table-row k-master-row local-time'>{localTimeValue}</span>
        </td>
      )
    } else {
      return(
        <td><span className='k-table-row k-master-row local-time' /></td>
      )
    }
  };

  const BatteryCellWithBackGroundComponent = (props: any) => {
    const batteryValue = props.dataItem.battery;
    //const batteryValueWithoutPercentage = batteryValue.trim().replace('%', '');
    
    if (batteryValue < 20) {
      return(
        <td>
          <svg className="battery-svg" viewBox="0 0 50 20">
            <rect className="battery-outline" x="1" y="1" width="40" height="18" rx="3" ry="3" fill="red" stroke="#333" stroke-width="1"/>
            <rect className="battery-nub" x="43" y="6" width="6" height="8" rx="2" ry="2" fill="red" stroke="#333"/>
            <text
              x="21"  y="12"  dominant-baseline="middle" text-anchor="middle"
              font-size="10"
              fill="white"
            >
              {batteryValue}% 
            </text>
          </svg>
          {/*<span className='batteryLow'>{batteryValue}%</span>*/}
        </td>
      )
    }
    else if (batteryValue >= 20 && batteryValue <= 83) {
      return(
        <td>
          <svg className="battery-svg" viewBox="0 0 50 20">
            <rect className="battery-outline" x="1" y="1" width="40" height="18" rx="3" ry="3" fill="orange" stroke="#333" stroke-width="1"/>
            <rect className="battery-nub" x="43" y="6" width="6" height="8" rx="2" ry="2" fill="orange" stroke="#333"/>
            <text
              x="21"  y="12"  dominant-baseline="middle" text-anchor="middle"
              font-size="10"
              fill="white"
            >
              {batteryValue}% 
            </text>
          </svg>
          {/*<span className='batteryMid'>{batteryValue}%</span>*/}
        </td>
      )
    } else {
      return(
        <td>
          <svg className="battery-svg" viewBox="0 0 50 20">
            <rect className="battery-outline" x="1" y="1" width="40" height="18" rx="3" ry="3" fill="green" stroke="#333" stroke-width="1"/>
            <rect className="battery-nub" x="43" y="6" width="6" height="8" rx="2" ry="2" fill="green" stroke="#333"/>
            <text
              x="21"  y="12"  dominant-baseline="middle" text-anchor="middle"
              font-size="10"
              fill="white"
            >
              {batteryValue}% 
            </text>
          </svg>
          {/*<span className='batteryHigh'>{batteryValue}%</span>*/}
        </td>
      )
    }        
  };
  
  const AlarmCellWithBackGroundComponent = (props: any) => {
    const alarmValue = props.dataItem.hasAlarm;

    const navigate = useNavigate()

    const showAlarm = () => {
      // console.log(props.dataItem.groupName);
      let deviceMac = props?.dataItem?.mac;
      if (deviceMac != null) {
        localStorage.setItem("deviceMacFromSynoptic", deviceMac);
      }
      navigate("/home/alarms")
    }
    
    if (alarmValue) {
      return(
        <td className="k-command-cell" style={{textAlign: 'center'}}>
          <button
            style={{ color: '#198792', border: 'none', background: 'none', outline: 'none' }}
            onClick={showAlarm}
          >
            <SvgIcon icon={alarm} className={'alarmButtonSynoptic'} size="medium" />
          </button>
        </td>
      )
    }
    else{
      return(
        <td>
          <span></span>
        </td>
      )
    }        
  };

  const currColumns: any = [
    //{ name:'editingCell', cell:{editingCell}, minWidth: 160, editable: false, filterable: false, show: true },
    { field: 'status', title: localizationService.toLanguageString('synoptic_devices.status', 'NOT_FOUND'), editable: false, filterable: false, show: true },
    { field: 'mac', title: "Mac", columnMenu: ColumnMenuCheckboxFilter, editable: false, show: true },
    { field: 'name', title: localizationService.toLanguageString('synoptic_devices.device_name', 'NOT_FOUND'), editor:"text", columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: true, show: true, hidden:false },
    { field: 'groupName', title: localizationService.toLanguageString('synoptic_devices.group_name', 'NOT_FOUND'), columnMenu: GroupCellWithColorComponent, filterable: false, editable: false, show: true, hidden:false },
    { field: 'jobName', title: localizationService.toLanguageString('synoptic_devices.job', 'NOT_FOUND'), columnMenu: JobCellWithColorComponent, filterable: false, editable: false, show: true, hidden:false },
    { field: 'zoneNameListJoined', title: localizationService.toLanguageString('synoptic_devices.zone', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: false, editable: false, show: true, hidden:false },
    { field: 'deviceTypeName', title: localizationService.toLanguageString('synoptic_devices.device_type_name', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, editable: false, show: true, hidden:false },
    { field: 'temperature', title: localizationService.toLanguageString('synoptic_devices.temperature', 'NOT_FOUND'), filter:"numeric", filterable: true, editable: false, show: true, hidden:false },
    { field: 'localTime', title: localizationService.toLanguageString('synoptic_devices.local_time', 'NOT_FOUND'), format: `{0:dd/MM/yyyy HH:mm:ss}`, filter:"date", filterable: true, editable: false, show: false, hidden:false },
    { field: 'dateTime', title: localizationService.toLanguageString('synoptic_devices.date_time', 'NOT_FOUND'), format:"{0:dd/MM/yyyy HH:mm:ss}", filter:"date", filterable: true, editable: false, show: true, hidden:false },
    { field: 'lastPositionDate', title: localizationService.toLanguageString('synoptic_devices.last_position_date', 'NOT_FOUND'), format:`{0:dd/MM/yyyy HH:mm:ss}`, filter:"date", filterable: true, editable: false, show: true, hidden:false },
    { field: 'localLastPositionDate', title: localizationService.toLanguageString('synoptic_devices.local_last_position_date', 'NOT_FOUND'), format:`{0:dd/MM/yyyy HH:mm:ss}`, filter:"date", filterable: true, editable: false, show: false, hidden:false },
    { field: 'customInfo', title: localizationService.toLanguageString('synoptic_devices.custom_info', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: false, show: true, hidden:false },
    { field: 'battery', title: localizationService.toLanguageString('synoptic_devices.battery', 'NOT_FOUND'), cell: BatteryCellWithBackGroundComponent, filterable: true, filter:"numeric", editable: false, show: true },
    { field: 'inCharge', title: localizationService.toLanguageString('synoptic_devices.inCharge', 'NOT_FOUND'), filterable: false, editable: false, show: true },
    { field: 'hasAlarm', title: localizationService.toLanguageString('synoptic_devices.alarm', 'NOT_FOUND'), cell: AlarmCellWithBackGroundComponent, filterable: false, editable: false, show: true },
    //...customInfoDictionaryColumns
  ];
  
  const [dataState, setDataState] = useState<any>({
    skip: 0,
    take: 10,
    data: devices,
    total: devices?.length,
    filter: { logic: 'and', filters: [] },
    sort: [{ field: 'mac', dir: 'desc' }]
  });

  //used when update device name
  const update = (dataItem: any) => {
   
    dataItem.inEdit = false;

    const devicesOnCopy = [...devicesOn];

    let index: any = devicesOn.findIndex((record: any) => record.mac === dataItem.mac);
    devicesOnCopy[index] = dataItem;

    var currentDate = new Date()
    var currentTime = formatDateFromIsoToString(currentDate)
    editFieldName(dataItem.mac, dataItem.name, currentTime)
    setDevicesOn(devicesOnCopy);
    
    setEdit((edit:any) => ({ ...edit, [dataItem.mac!]: false }));
    setEditMode(false)
  };

  const cancel = (dataItem: any) => {
    setDevicesOn(devicesOn);
    setEdit((edit:any) => ({ ...edit, [dataItem.mac!]: false }));
    setEditMode(false)
  };

  const enterEdit = (dataItem: any) => {

    setEditMode(true)
    
    const newDevice = devicesOn.map((item: any) =>
      item.mac === dataItem.mac
        ?
        {
          ...item,
          inEdit: true
        }
        :
        item
    )
    setFilterName(['nameEdit'])
    localStorage.setItem("filterNameSynoptic", JSON.stringify(['nameEdit']));
    setDevicesOn(newDevice);
    setEdit((edit:any) => ({ ...edit, [dataItem.mac!]: true }));
    
  };
  const itemChange = (event: GridItemChangeEvent) => {
    const newData: any = devicesOn.map((device: any) =>
      device.mac === event.dataItem.mac
        ? { ...device, [event.field || ""]: event.value }
        : device
    );
    setDevicesOn(newData);

    setEditMode(false)
  };

  const commandCellProps = {
    edit: enterEdit,
    update: update,
    cancel: cancel,
    editField: editField
  };

  const saveStateToLocalStorage = () => {
    const state = { dataState, columns };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(state));
  };

  const loadStateFromLocalStorage = () => {
    const storedFilter = localStorage.getItem(`gridSynopticState: ${userEmail}`);
    if (storedFilter) {
      const { dataState: savedDataState, columns: savedColumns } = JSON.parse(storedFilter);
  
      if (savedDataState?.filter != null) {
        // Funzione ricorsiva per gestire qualsiasi struttura di filtri
        const updateFilterValues = (filter: any): any => {
          if (Array.isArray(filter.filters)) {
            return {
              ...filter,
              filters: filter.filters.map(updateFilterValues),
            };
          } else {
            const field = filter?.field;
            let value = filter?.value;
  
            if (['dateTime', 'localTime', 'localLastPositionDate', 'lastPositionDate'].includes(field)) {
              value = new Date(value);
            }
  
            return {
              ...filter,
              value,
            };
          }
        };
  
        const updatedFilter = updateFilterValues(savedDataState.filter);
  
        const updatedSavedDataState = {
          ...savedDataState,
          filter: updatedFilter,
        };
  
        setDataState(updatedSavedDataState);
      } else {
        setDataState(savedDataState);
      }
  
      // Column management remains unchanged
      let currentColumn: any = savedColumns?.map((column: any, id: any) => {
        if (column.id == id) {
          return { ...column, show: !column.show };
        }
        return column;
      });
  
      return storedFilter ? storedFilter : currentColumn;
    }
  };
  
  useEffect(() => {
    setColumns(currColumns);
  }, []);

  //toolbar buttons functions
  const onChange = (event: any) => {
    const field = event.target.name;
    const newColumns = columns.map((column: any) => {
      if (column.field === field) {
        return { ...column, show: !column.show };
      }
      return column;
    });
    setColumns(newColumns);
    
    const state = { dataState, columns: newColumns };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(state));
  };

  const handleColumnReorder = (event: any) => {
    setColumns(event?.columns);
    saveStateToLocalStorage();
  };

  const onColumnResize = (event: GridColumnResizeEvent) => {
    setColumns(event?.columns);
    saveStateToLocalStorage();
  };
  
  const onDataStateChange = (event: GridDataStateChangeEvent) => {

    const { dataState } = event;
    if (dataState) {
      const { filter, sort, skip, take } = dataState;
      const updatedFilter = filter || dataState.filter;
      const updatedSkip = skip !== undefined ? skip : dataState.skip;
      const updatedTake = take !== undefined ? take : dataState.take;
      setDataState((prevState: any) => ({
        ...prevState,
        filter: updatedFilter,
        sort: sort,
        skip: updatedSkip,
        take: updatedTake
      }));
    }
    const state = { dataState, columns };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(state));
  };

  const CellRender = (props: any) => {
    return (
      <td
          {...props.tdProps}
          colSpan={1}
      >
          {props.children}
      </td>
    );
  }

  const sanitizeFilter = (filter: any) => {
    if (filter && typeof filter === 'object' && Array.isArray(filter.filters)) {
      const flatFilters = filter.filters.flatMap((f: any) =>
        Array.isArray(f.filters) ? f.filters : [f]
      );
  
      return {
        logic: filter.logic || 'and',
        filters: flatFilters
      };
    }
    return { logic: 'and', filters: [] };
  };

  const safeFilter = sanitizeFilter(dataState?.filter);

  const processedData = kendoProcess(devicesOn, {
    filter: dataState?.filter ?? safeFilter, 
    sort: dataState?.sort,
    skip: dataState?.skip,
    take: dataState?.take,
  });

  const onPageChange = (event: any) => {
    const newSkip = event?.page?.skip;
    const newTake = event?.page?.take;
    setDataState((prevState: any) => ({
      ...prevState,
      skip: newSkip,
      take: newTake
    }));
  };

  const _excelExport = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_excelExport.current !== null) {
      _excelExport?.current?.save(devicesOn);
    }
  };

  const _pdfExport = useRef<GridPDFExport | null>(null);
  const pdfExport = () => {
    if (_pdfExport.current !== null) {
      //if (devices.length < 500) {
      //  _pdfExport.current.save(devices);
      //} else {
      //  _pdfExport.current.save();
      //}
      _pdfExport?.current?.save(devicesOn);
    }
  };

  const anchor = useRef<any>(null);
  const [showPopupSaveConfig, setShowPopupSaveConfig] = useState(false);

  const onClickSaveConfig = () => {
    setShowPopupSaveConfig(!showPopupSaveConfig);

    setTimeout(() => {
      setShowPopupSaveConfig(showPopupSaveConfig);
    }, 3000)

  };

  const visibleColumns = useMemo(() => columns?.filter((col:any) => col?.show), [columns]);

  const handleExportCsv = async () => {
    
    const filteredData:any = devicesOn.map((item:any) => {
      const filteredItem:any = {};
      visibleColumns.forEach((col:any) => {
        filteredItem[col.field] = item[col.field];
      });
      return filteredItem;
    });

    try {
        const csv = await json2csv(filteredData);

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "data.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (err) {
        console.error(err);
    }
  };

  const onToggleLive = (e:any) => {
    
    const liveStatus: any = e.value ?? e
    localStorage.setItem('liveStatus', JSON.stringify(liveStatus))
    setLiveStatus(liveStatus)
    
    if(liveStatus){
      const minutesBefore: any = statusDevice 

      let liveDevicesToShowInGrid:any = []
      let dateBeforeMinutes:any = ''
      //devicesList[0].lastPositionDate = '12/04/2024 8:00:00 AM' // for test
      devicesOn.map((device: any) => {      
        if(device.dateTime == null || device.dateTime == 0){
          return
        } else{        
          dateBeforeMinutes = getDateRange(minutesBefore)
          const localTimeString = device.dateTime.toLocaleString(navigator.language, { 
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true // Force 12-hour format with AM/PM
          });    
          if(compareDateTimes(localTimeString, dateBeforeMinutes) == 1){
            liveDevicesToShowInGrid.push(device)
          }      
        }
      })
      setDevicesOn(liveDevicesToShowInGrid)
    }else{
      if(onlyBound){
        getDeviceList(onlyBound)
      }else{
        getDeviceList()        
      }
    }    
  }

  const getDateRange = (minutes:any) => {
    // Create a Date object from the input string
    const currentDate = new Date();

    // 2. Calculate milliseconds to subtract:
    const millisecondsToSubtract = minutes * 60 * 1000; // minutes * seconds * milliseconds

    // 3. Subtract the milliseconds:
    currentDate.setTime(currentDate?.getTime() - millisecondsToSubtract);

    const localTimeString = currentDate.toLocaleString(navigator.language, { 
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true // Force 12-hour format with AM/PM
    });   

    return localTimeString
  }
  
  // Function to convert AM/PM formatted time string into a Date object
  const convertToDate = (dateString:any) => {
    const [datePart, timePart, ampm] = dateString?.replace(/,/g, '').split(' '); // Split date and time part
    const [day, month, year] = datePart?.split('/'); // Split date into day, month, and year
       
    let [hours, minutes, seconds] = timePart?.split(':'); // Split time into hours, minutes, and seconds
    
    hours = parseInt(hours);
    minutes = parseInt(minutes);
    seconds = parseInt(seconds);

    // Convert the 12-hour format to 24-hour format
    if (ampm === 'PM' && hours < 12) hours += 12;
    if (ampm === 'AM' && hours === 12) hours = 0;

    // Create a new Date object based on the parsed values
    return new Date(year, month - 1, day, hours, minutes, seconds);
  }

  const compareDateTimes = (date1Str:any, date2Str:any) => {
    // Parse the dates into Date objects    
    const date1 = convertToDate(date1Str);
    const date2 = convertToDate(date2Str);
  
    // Compare the milliseconds
    if (date1?.getTime() < date2?.getTime()) {
      return -1; // date1 is earlier
    } else if (date1?.getTime() > date2?.getTime()) {
      return 1; // date1 is later
    } else {
      return 0; // dates are equal
    }
  }

  const onToggleOnlyBound = (e:any) => {    
    const onlyBound: any = e.value
    localStorage.setItem('onlyBound', JSON.stringify(onlyBound))
    setOnlyBound(onlyBound)  
    if(liveStatus){
      onToggleLive(liveStatus)
    }else{
      getDeviceList(onlyBound)  
    }  
  }

  const IconHeaderCell = (props: any) => {  

    localStorage.setItem('filterNameSynoptic', JSON.stringify(filterName));
    
    localStorage.removeItem('filterOpenedColumn');
    const savedFilterName:any = localStorage.getItem('filterNameSynoptic');

    return savedFilterName?.includes(props.field) ?       
      <HeaderThElement columnId={props.thProps?.columnId || ''} {...props.thProps} >
        <span style={{display:'inline-flex'}}>{props.children} <SvgIcon icon={filterIcon} size="medium"/></span>
      </HeaderThElement>    
    :
      <HeaderThElement columnId={props.thProps?.columnId || ''} {...props.thProps} >
        {props.children}
      </HeaderThElement>
  };
 
  //const DefaultIconHeaderCell = (props: any) => {    
  //  //console.log('default');
  //  return( 
  //    <HeaderThElement columnId={props.thProps?.columnId || ''} {...props.thProps} >
  //      {props.children}
  //    </HeaderThElement>
  //  )
  //};

  return (
    <div 
      style={{ 
        display: 'grid',
        width: '98%',
        margin: '10px',        
        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 15px 5px'        
      }}
    >
      {error && <p className="text-danger" style={{ zIndex: 999, position: 'relative', width: '65%' }}>{error}</p>}
      {loading && loadingPanel}
      <ExcelExport data={devicesOn} ref={_excelExport}>
        <Grid
          data={processedData}
          dataItemKey={"mac"}
          cells={{ data: CellRender}}
          sortable={true}
          defaultTake={10}
          defaultSkip={0}
          pageable={{ type: 'input', buttonCount: 5, pageSizes: [5, 10, 50, 100], info: true }}
          reorderable={false}
          sort={dataState?.sort}
          filter={dataState?.filter}
          onColumnResize={onColumnResize}
          onDataStateChange={onDataStateChange}
          onPageChange={onPageChange}
          onItemChange={itemChange}
          edit={edit}
          editable={true}
          scrollable={'none'}
        >
          <GridToolbar>
            <div>
              <h3>{localizationService.toLanguageString('synoptic_devices.title', 'NOT_FOUND')}</h3>
            </div>
            <div style={{ marginLeft: '25px',  whiteSpace: 'nowrap', alignItems: 'flex-end'}}>
                <Switch
                  onChange={onToggleLive}
                  checked={liveStatus}
              />
              <label style={{margin:'0 auto'}} className="k-ml-3">{localizationService.toLanguageString('synoptic_devices.live', 'NOT_FOUND')}</label>
            </div>
            <div style={{ marginLeft: '25px',  whiteSpace: 'nowrap', alignItems: 'flex-end'}}>
                <Switch
                  onChange={onToggleOnlyBound}
                  checked={onlyBound}
              />
              <label style={{margin:'0 auto'}} className="k-ml-3">{localizationService.toLanguageString('synoptic_devices.only_binded', 'NOT_FOUND')}</label>
            </div>
            <Popup
              anchor={anchor.current && anchor.current.element}
              show={showPopupSaveConfig}
              popupClass={"popup-content-save-synoptic-config"}
            >
              {localizationService.toLanguageString('synoptic_devices.saved_config', 'NOT_FOUND')}
            </Popup>
            <div style={{ right: '15px', position: 'absolute', display: 'flex', gap: '8px' }}>
              <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />              
              <div>
                  <Button
                    themeColor={"primary"}
                    size="small"
                    title={localizationService.toLanguageString('synoptic_devices.export_to_csv', 'NOT_FOUND')}
                    svgIcon={fileCsvIcon}
                    onClick={handleExportCsv}
                  />
              </div>
              <div>
                <Button
                  onClick={pdfExport}
                  themeColor={"primary"}
                  size="small"
                  title={localizationService.toLanguageString('synoptic_devices.export_to_pdf', 'NOT_FOUND')}
                  svgIcon={filePdfIcon}
                />
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.export_to_excel', 'NOT_FOUND')}
                  themeColor={"primary"}
                  size="small"
                  svgIcon={fileExcelIcon}
                  onClick={excelExport}
                />
              </div>             
              {/*<div>
                <Button
                  ref={anchor}
                  title={localizationService.toLanguageString('synoptic_devices.save_config', 'NOT_FOUND')}
                  themeColor={'primary'}
                  size="small"
                  svgIcon={saveIcon}
                  onClick={() => {
                    saveStateToLocalStorage();
                    onClickSaveConfig()
                  }}
                >
                </Button>
              </div>
              <div>
                <Button
                  themeColor={'primary'}
                  size="small"
                  svgIcon={uploadIcon}
                  onClick={()=>loadStateFromLocalStorage()}
                  title={localizationService.toLanguageString('synoptic_devices.load_config', 'NOT_FOUND')}
                >
                </Button>
              </div> */}
            </div>
          </GridToolbar>
          { columns[2]?.show &&
            <Column
              cells={{ data: (props:any) => <EditingCell  {...props} {...commandCellProps} />}}
              filterable={false}
              sortable={false}
            />
          }
          <Column
            cells={{ data: LinkMapCell  }}
            editable={false}
            filterable={false}
            sortable={false}
          />
          <Column
            cells={{ data: LinkDeviceManagement  }}
            editable={false}
            filterable={false}
            sortable={false}
          />
          {
            columns?.length > 0 ?
              columns?.map((column: any, index: any) =>
                column.show && (
                  column.field == 'groupName' ?
                  <Column              
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.group_name', 'NOT_FOUND')}
                    cells={{ 
                      data: (props) => <GroupCellWithColorComponent  {...props} color={column?.groupColor} />,
                      headerCell: IconHeaderCell
                    }}
                    editable={false}
                    filterable={true}
                    columnMenu= {ColumnMenuCheckboxFilter}
                    sortable={true}
                  />
                  :
                  column.field == 'jobName' ?
                  <Column              
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.job', 'NOT_FOUND')}
                    cells={{ 
                      data: (props) => <JobCellWithColorComponent  {...props} color={column?.jobColor} />,
                      headerCell:IconHeaderCell
                    }}
                    editable={false}
                    filterable={true}
                    columnMenu= {ColumnMenuCheckboxFilter}
                    sortable={true}
                  />
                  :
                  column.field == 'temperature' ?
                  <Column              
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.temperature', 'NOT_FOUND')}
                    cells={{ 
                      data: (props) => <TemperatureCellWithBackGroundComponent  {...props} />,
                      headerCell:IconHeaderCell
                    }}
                    editable={false}
                    filterable={true}
                    columnMenu= {ColumnMenuCheckboxFilter}
                    filter='numeric'
                    sortable={true}
                  />
                  :
                  column.field == 'dateTime' ?
                  <Column              
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.date_time', 'NOT_FOUND')}
                    cells={{ 
                      data: (props) => <DateTimeCellWithBackGroundComponent  {...props} />,
                      headerCell:IconHeaderCell
                    }}
                    editable={false}
                    format={column.format}
                    columnMenu={(props) => (
                      <GridColumnMenuFilter
                        {...props}                        
                        alwaysExpand
                        filterUI={CustomGridFilterDateTime}
                        onExpandChange={() => {}}
                        onCloseMenu={() => {
                          const filterName = props.column.field;      
                          setFilterName((prevFilterName:any) => {
                            return prevFilterName.filter((item:any) => item !== filterName);
                          });
                          localStorage.removeItem('filterOpenedColumn');
                        }}
                      />
                    )}
                    filter={'date'}
                    sortable={true}
                  />
                  :
                  column.field == 'lastPositionDate' ?
                  <Column              
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.last_position_date', 'NOT_FOUND')}
                    cells={{ 
                      data: (props) => <LastPositionDateCellWithBackGroundComponent  {...props} />,
                      headerCell:IconHeaderCell
                    }}
                    editable={false}
                    format={column.format}
                    columnMenu={(props) => (
                      <GridColumnMenuFilter
                        {...props}                        
                        alwaysExpand
                        filterUI={CustomGridFilterDateTime}
                        onExpandChange={() => {}}
                        onCloseMenu={() => {
                          const filterName = props.column.field;      
                          setFilterName((prevFilterName:any) => {
                            return prevFilterName.filter((item:any) => item !== filterName);
                          });
                          localStorage.removeItem('filterOpenedColumn');
                        }}
                      />
                    )}
                    filter={'date'}
                    sortable={true}
                  />
                  :
                  column.field == 'localLastPositionDate' ?
                  <Column              
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.local_last_position_date', 'NOT_FOUND')}
                    cells={{ 
                      data: (props) => <LocalLastPositionDateCellWithBackGroundComponent  {...props} />,
                      headerCell:IconHeaderCell
                    }}
                    editable={false}
                    format={column.format}
                    columnMenu={(props) => (
                      <GridColumnMenuFilter
                        {...props}                        
                        alwaysExpand
                        filterUI={CustomGridFilterDateTime}
                        onExpandChange={() => {}}
                        onCloseMenu={() => {
                          const filterName = props.column.field;      
                          setFilterName((prevFilterName:any) => {
                            return prevFilterName.filter((item:any) => item !== filterName);
                          });
                          localStorage.removeItem('filterOpenedColumn');
                        }}
                      />
                    )}
                    filter={'date'}
                    sortable={true}
                  />
                  :
                  column.field == 'localTime' ?
                  <Column              
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.local_time', 'NOT_FOUND')}
                    cells={{ 
                      data: (props) => <LocalTimeCellWithBackGroundComponent  {...props} />,
                      headerCell:IconHeaderCell
                    }}
                    editable={false}
                    format={column.format}
                    columnMenu={(props) => (
                      <GridColumnMenuFilter
                        {...props}                        
                        alwaysExpand
                        filterUI={CustomGridFilterDateTime}
                        onExpandChange={() => {}}
                        onCloseMenu={() => {
                          const filterName = props.column.field;      
                          setFilterName((prevFilterName:any) => {
                            return prevFilterName.filter((item:any) => item !== filterName);
                          });
                          localStorage.removeItem('filterOpenedColumn');
                        }}
                      />
                    )}
                    filter={'date'}
                    sortable={true}
                  />                  
                  :
                  column.field == 'customInfo' ?
                  <Column              
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.custom_info', 'NOT_FOUND')}
                    columnMenu= {CustomInfoCheckboxFilter}
                    editable={false}
                    filterable={true}
                    format={column.format}
                    filter={column.filter}
                    sortable={true}
                    cells={{headerCell:IconHeaderCell}}
                  />                  
                  :
                  column.field == 'battery' ?
                  <Column              
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.battery', 'NOT_FOUND')}
                    cells={{ 
                      data: (props) => <BatteryCellWithBackGroundComponent  {...props} />,
                      headerCell:IconHeaderCell
                    }}
                    editable={false}
                    filterable={true}
                    filter='numeric'
                    columnMenu= {ColumnMenuNumericFilter}
                    sortable={true}
                  />
                  :
                  column.field == 'status' ?
                  <Column
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.status', 'NOT_FOUND')}
                    cells={{ 
                      data: (props) => <StatusCellWithColorComponent  {...props} />,
                      headerCell:IconHeaderCell
                    }}
                    editable={false}
                    filterable={false}
                    sortable={true}
                  />
                  :
                  column.field == 'inCharge' ?
                  <Column
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.inCharge', 'NOT_FOUND')}
                    cells={{ 
                      data: (props) => <InChargeCellWithColorComponent  {...props} />,
                      headerCell:IconHeaderCell
                    }}
                    editable={false}
                    filterable={false}
                    sortable={true}
                  />
                  :
                  column.field == 'hasAlarm' ?
                  <Column
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.alarm', 'NOT_FOUND')}
                    cells={{ 
                      data: (props) => <AlarmCellWithBackGroundComponent  {...props} />,
                      headerCell:IconHeaderCell
                    }}
                    editable={false}
                    filterable={false}
                    sortable={true}
                  />
                  :
                  column.field == 'name' ?
                  <Column
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.device_name', 'NOT_FOUND')}
                    filterable={true}
                    columnMenu= {ColumnMenuCheckboxFilter}
                    filter={column?.filter}
                    format={column?.format}             
                    editable={column?.editable}
                    hidden={column?.hidden}
                  />
                  :
                  column.field == 'zoneNameListJoined' ?
                  <Column
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.zone', 'NOT_FOUND')}
                    filterable={true}
                    columnMenu= {ColumnMenuCheckboxFilter}
                    cells={{ 
                      headerCell:IconHeaderCell
                    }}
                    filter={column?.filter}
                    format={column?.format}             
                    editable={column?.editable}
                    hidden={column?.hidden}
                  />
                  :
                  column.field == 'deviceTypeName' ?
                  <Column
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.device_type_name', 'NOT_FOUND')}
                    filterable={true}
                    columnMenu= {ColumnMenuCheckboxFilter}
                    cells={{ 
                      headerCell:IconHeaderCell
                    }}
                    filter={column?.filter}
                    format={column?.format}             
                    editable={column?.editable}
                    hidden={column?.hidden}
                  />
                  : 
                  <Column
                    field={column?.field}
                    title={column?.title}
                    filterable={true}
                    columnMenu= {ColumnMenuCheckboxFilter}
                    filter={column?.filter}
                    format={column?.format}             
                    editable={column?.editable}
                    hidden={column?.hidden}
                    editor={column?.editor}
                    cells={{headerCell:IconHeaderCell}}
                    width={'2000'}
                  />
                )
              )
              :
              columns?.columns?.map((column: any, index: any) =>
                columns.show && (
                  <Column
                    key={index}
                    field={column?.field}
                    title={column?.title}
                    filterable={column?.filterable}
                    columnMenu={ColumnMenuCheckboxFilter}
                    filter={column?.filter}
                    format={column?.format}
                    editable={column?.editable}
                    hidden={column?.hidden}
                    editor={column?.editor}
                  />
                )
              )
          }
        </Grid>
      </ExcelExport>
      
      <GridPDFExport ref={_pdfExport} margin="1cm">
        {
          columns?.length > 0 ?
            columns?.map((column: any, index: any) =>
              column.show && (
                <Column
                  key={index}
                  field={column?.field}
                  title={column?.title}
                  filterable={column?.filterable}
                  columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                  className='k-text-center'
                  filter={column?.filter}
                  format={column?.format}
                  editable={column?.editable}
                  editor={column?.editor}
                />
              )
            )
            :
            columns?.columns?.map((column: any, index: any) =>
              columns.show && (
                <Column
                  key={index}
                  field={column?.field}
                  title={column?.title}
                  filterable={column?.filterable}
                  columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                  className='k-text-center'
                  filter={column?.filter}
                  format={column?.format}
                  editable={column?.editable}
                  editor={column?.editor}
                />
              )
            )
        }
        <Grid
          data={devicesOn}
          dataItemKey={"mac"}
          skip={dataState.skip}
          take={dataState.take}
          total={countDevicesOn}
          filterable={false}
          sortable={true}
          reorderable={false}
          pageable={{ type: 'input', buttonCount: 5, pageSizes: [5, 10, 50, 100], info: true }}
          sort={dataState.sort}
          filter={dataState.filter}
          //onFilterChange={onFilterChange}
          onColumnReorder={handleColumnReorder}
          onColumnResize={onColumnResize}
          onDataStateChange={onDataStateChange}
          onPageChange={onPageChange}
          onItemChange={itemChange}
          edit={edit}
          editable={true}
          scrollable={'none'}
        >
          <GridToolbar>
            <div>
              <h3>{localizationService.toLanguageString('synoptic_devices.title', 'NOT_FOUND')}</h3>
            </div>
            <div style={{ right: '15px', float: 'right', position: 'absolute', display: 'flex', gap: '8px' }}>
              <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />              
              <div>
                <CSVLink data={json2csv(devices)} separator={","}>
                  <Button
                    title={localizationService.toLanguageString('synoptic_devices.export_to_csv', 'NOT_FOUND')}
                    themeColor={"primary"}
                    size="small"
                    svgIcon={fileCsvIcon}
                  />
                </CSVLink>
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.export_to_pdf', 'NOT_FOUND')}
                  onClick={pdfExport}
                  themeColor={"primary"}
                  size="small"
                  svgIcon={filePdfIcon}
                />
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.export_to_excel', 'NOT_FOUND')}
                  themeColor={"primary"}
                  size="small"
                  svgIcon={fileExcelIcon}
                  onClick={excelExport}
                />
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.save_config', 'NOT_FOUND')}
                  themeColor={'primary'}
                  size="small"
                  svgIcon={saveIcon}
                  onClick={saveStateToLocalStorage}
                >
                </Button>
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.load_config', 'NOT_FOUND')}
                  themeColor={'primary'}
                  size="small"
                  svgIcon={uploadIcon}
                  onClick={loadStateFromLocalStorage}
                >
                </Button>
              </div>
            </div>
          </GridToolbar>
          <Column
            cells={{ data: (props:any) => <EditingCell  {...props} {...commandCellProps} />}}
            editable={false}
            filterable={false}
          />
          <Column
            cells={{ data: LinkMapCell  }}
            editable={false}
            filterable={false}
          />
          <Column
            cells={{ data: LinkDeviceManagement  }}
            editable={false}
            filterable={false}
            sortable={false}
          />
          {
            columns?.length > 0 ?
              columns?.map((column: any, index: any) =>
                column.show && (
                  <Column
                    key={index}
                    field={column?.field}
                    title={column?.title}
                    filterable={column?.filterable}
                    columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                    className='k-text-center'
                    filter={column?.filter}
                    format={column?.format}
                    editable={column?.editable}
                    editor={column?.editor}
                  />
                )
              )
              :
              columns?.columns?.map((column: any, index: any) =>
                columns.show && (
                  <Column
                    key={index}
                    field={column?.field}
                    title={column?.title}
                    filterable={column?.filterable}
                    columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                    className='k-text-center'
                    filter={column?.filter}
                    format={column?.format}
                    editable={column?.editable}
                    editor={column?.editor}
                  />
                )
              )

          }
          <Column              
            field='status'
            title={localizationService.toLanguageString('synoptic_devices.status', 'NOT_FOUND')}
            cells={{ data: (props) => <StatusCellWithColorComponent  {...props} />}}
            editable={false}
            filterable={false}
            sortable={true}
          />                  
          <Column              
            field='groupName'
            title={localizationService.toLanguageString('synoptic_devices.group_name', 'NOT_FOUND')}
            cells={{ data: (props) => <GroupCellWithColorComponent  {...props} />}}
            editable={false}
            filterable={false}
            columnMenu= {ColumnMenuCheckboxFilter}
            sortable={true}
          />                  
          <Column              
            field='jobName'
            title={localizationService.toLanguageString('synoptic_devices.job', 'NOT_FOUND')}
            cells={{ data: (props) => <JobCellWithColorComponent  {...props} />}}
            editable={false}
            filterable={false}
            columnMenu= {ColumnMenuCheckboxFilter}
            sortable={true}
          />            
          <Column              
            field='dateTime'
            title={localizationService.toLanguageString('synoptic_devices.date_time', 'NOT_FOUND')}
            cells={{ data: (props) => <DateTimeCellWithBackGroundComponent  {...props} />}}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}                        
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => {}}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter={'date'}
            sortable={true}
          />      
          <Column              
            field='lastPositionDate'
            title={localizationService.toLanguageString('synoptic_devices.last_position_date', 'NOT_FOUND')}
            cells={{ data: (props) => <LastPositionDateCellWithBackGroundComponent  {...props} />}}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}                        
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => {}}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter={'date'}
            sortable={true}
          />
          <Column              
            field='localLastPositionDate'
            title={localizationService.toLanguageString('synoptic_devices.local_last_position_date', 'NOT_FOUND')}
            cells={{ data: (props) => <LocalLastPositionDateCellWithBackGroundComponent  {...props} />}}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}                        
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => {}}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter='date'
            sortable={true}
          />
          <Column
            field= 'localTime'
            title={localizationService.toLanguageString('synoptic_devices.local_time', 'NOT_FOUND')}
            cells={{ data: (props) => <LocalTimeCellWithBackGroundComponent  {...props} />}}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}                        
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => {}}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter='date'
            sortable={true}
          />
          <Column
            field= 'temperature'
            title={localizationService.toLanguageString('synoptic_devices.temperature', 'NOT_FOUND')}
            cells={{ data: (props) => <TemperatureCellWithBackGroundComponent  {...props} />}}
            editable={false}
            filterable={true}
            filter='numeric'
            sortable={true}
          />
          <Column
            field= 'battery'
            title={localizationService.toLanguageString('synoptic_devices.battery', 'NOT_FOUND')}
            cells={{ data: (props) => <BatteryCellWithBackGroundComponent  {...props} />}}
            editable={false}
            filterable={true}
            filter='numeric'
            sortable={true}
          />
          <Column
            field= 'inCharge'
            title={localizationService.toLanguageString('synoptic_devices.inCharge', 'NOT_FOUND')}            
            cells={{ data: (props) => <InChargeCellWithColorComponent  {...props} />}}
            editable={false}
            filterable={false}
            sortable={true}
          />
          <Column
            field='alarm'
            title={localizationService.toLanguageString('synoptic_devices.alarm', 'NOT_FOUND')}            
            cells={{ data: (props) => <AlarmCellWithBackGroundComponent  {...props} />}}
            editable={false}
            filterable={false}
            sortable={false}
          />
        </Grid>
      </GridPDFExport>
    </div>
  );
};